// src/components/jobs/JobCard.jsx
import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { Bookmark, BookmarkCheck } from "lucide-react";
import styles from "./JobCard.module.css";

const SOURCE_COLORS = {
  Adzuna:    "#00C896",
  JSearch:   "#7B82A0",
  Freelance: "#FF6B35",
  Manual:    "#F5A623",
  LinkedIn:  "#0A66C2",
};

const REQUIRES_CREDIT = () => true;

const PLATFORM_NAMES = /freelancer\.com|gumtree|pnet|careers24|indeed|adzuna|jsearch|linkedin/i;
const whitelabelCompany = (name) =>
  !name || PLATFORM_NAMES.test(name) ? "GigScout Client" : name;

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 5)  return "Just now";
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  === 1) return "Yesterday";
  if (days  < 7)  return `${days}d ago`;
  if (days  < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function JobCard({ job, adminView = false }) {
  const { credits, unlockJob, isJobUnlocked, toggleSave, isJobSaved, currentUser } = useAuth();
  const [unlocking,   setUnlocking]   = useState(false);
  const [unlockError, setUnlockError] = useState("");
  const [justUnlocked, setJustUnlocked] = useState(false);
  const [saving,      setSaving]      = useState(false);

  const saved = isJobSaved?.(job.id) ?? false;

  const handleSave = async () => {
    if (!currentUser || saving) return;
    setSaving(true);
    try { await toggleSave?.(job.id); } finally { setSaving(false); }
  };

  const needsCredit = REQUIRES_CREDIT(job.source) && !adminView;
  const unlocked    = isJobUnlocked(job.id) || justUnlocked || !needsCredit;
  const sourceColor = SOURCE_COLORS[job.source] || SOURCE_COLORS.JSearch;

  const handleUnlock = async () => {
    if (!currentUser) return;
    setUnlocking(true);
    setUnlockError("");
    try {
      await unlockJob(job);
      setJustUnlocked(true);
    } catch (err) {
      setUnlockError(
        err.message === "Insufficient credits"
          ? "Not enough credits. Buy more to apply."
          : "Failed to unlock. Try again."
      );
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.top}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>{job.title}</h3>
          <button
            className={`${styles.bookmarkBtn} ${saved ? styles.bookmarkSaved : ""}`}
            onClick={handleSave}
            disabled={saving}
            title={saved ? "Remove bookmark" : "Save job"}
          >
            {saved
              ? <BookmarkCheck size={16} strokeWidth={2} />
              : <Bookmark size={16} strokeWidth={2} />
            }
          </button>
        </div>
        {job.company && (
          <p className={styles.company}>{whitelabelCompany(job.company)}</p>
        )}
        <div className={styles.metaRow}>
          <p className={styles.location}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            {job.location}
          </p>
          {job.salary && (
            <span className={styles.salary}>{job.salary}</span>
          )}
        </div>
      </div>


      {job.skills?.length > 0 && (
        <div className={styles.skills}>
          {job.skills.slice(0, 4).map(s => (
            <span key={s} className={styles.skillChip}>{s}</span>
          ))}
        </div>
      )}

      {unlocked ? (
        <p className={styles.description}>
          {job.description?.slice(0, 200)}
          {job.description?.length > 200 ? "…" : ""}
        </p>
      ) : (
        <p className={styles.descriptionLocked}>
          {job.description?.slice(0, 80)}
          {job.description?.length > 80 ? "…" : ""}
          <span className={styles.descBlur}> Unlock to see full details →</span>
        </p>
      )}

      <div className={styles.footer}>
        <span className={styles.date}>{timeAgo(job.datePosted)}</span>

        {unlocked ? (
          job.url ? (
            <a
              href={job.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.applyBtn}
            >
              Apply →
            </a>
          ) : (
            <span className={styles.applyBtnDisabled}>Link unavailable</span>
          )
        ) : (
          <div className={styles.lockWrap}>
            {unlockError && (
              <span className={styles.unlockError}>{unlockError}</span>
            )}
            <button
              className={styles.unlockBtn}
              onClick={handleUnlock}
              disabled={unlocking}
            >
              {unlocking ? (
                <span className={styles.spinner} />
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                  2 credits
                </>
              )}
            </button>
            <span className={styles.creditHint}>{credits} left</span>
          </div>
        )}
      </div>

      {needsCredit && !unlocked && (
        <div className={styles.lockedOverlay}>
          <span className={styles.lockedNote}>
            2 credits · contact details + apply link revealed
          </span>
        </div>
      )}
    </div>
  );
}
